import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Heart, Briefcase, Sparkles, User, Moon, Sun, Compass, Eye, FileText, Check, Clock, Shield, MapPin, Calendar, BookOpen, Gem, Target, Users, Search, Lightbulb } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { Rating } from '../components/ui/Rating';
import { ServicePurchaseSection } from '../components/ServicePurchaseSection';
interface NatalChartPageProps {
  onNavigate: (page: string) => void;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-21364">
      {children}
    </motion.section>;
}
export function NatalChartPage({
  onNavigate
}: NatalChartPageProps) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const scrollToCta = () => {
    ctaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="w-full bg-slate-50 min-h-screen overflow-hidden" data-id="element-21365">
      {/* ═══════════════════════════════════════════ */}
      {/* 1. HERO — без цен, фокус на ценности */}
      {/* ═══════════════════════════════════════════ */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 sm:pt-8 pb-14 sm:pb-24 lg:pb-32" data-id="element-21366">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-indigo-600/15 rounded-full blur-3xl pointer-events-none" data-id="element-21367" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-purple-600/15 rounded-full blur-3xl pointer-events-none" data-id="element-21368" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-21369">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Натальная карта'
        }]} className="mb-6 sm:mb-12 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-21370" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-21371">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-21372">
              <motion.h1 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-[1.15]" data-id="element-21373">
                Узнайте свою натальную карту и раскройте потенциал своей
                личности
              </motion.h1>

              <motion.p variants={staggerItem} className="text-sm sm:text-base md:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed" data-id="element-21374">
                Натальная карта — это астрологическая схема положения планет в
                момент вашего рождения. Она раскрывает особенности характера,
                таланты, жизненные задачи и ключевые периоды развития.
              </motion.p>

              <motion.div variants={staggerItem} className="mb-6 sm:mb-10" data-id="element-21375">
                <Button size="lg" onClick={scrollToCta} className="text-base sm:text-lg px-6 sm:px-8 shadow-lg shadow-indigo-500/25" data-id="element-21376">
                  Рассчитать мою натальную карту
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-21377" />
                </Button>
              </motion.div>

              <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-21378">
                {[{
                icon: <Eye className="w-4 h-4" data-id="element-21379" />,
                text: 'Глубокий анализ личности'
              }, {
                icon: <Sparkles className="w-4 h-4" data-id="element-21380" />,
                text: 'Понимание сильных сторон'
              }, {
                icon: <Target className="w-4 h-4" data-id="element-21381" />,
                text: 'Рекомендации для жизни и карьеры'
              }].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-21382">
                    <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-300 flex-shrink-0" data-id="element-21383">
                      {item.icon}
                    </div>
                    {item.text}
                  </div>)}
              </motion.div>
            </motion.div>

            {/* Illustration */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.2
          }} className="hidden lg:flex justify-center relative" data-id="element-21384">
              <div className="w-[420px] h-[420px] rounded-full border border-white/10 flex items-center justify-center relative" data-id="element-21385">
                <div className="absolute inset-0 rounded-full border border-indigo-500/20 animate-[spin_60s_linear_infinite]" data-id="element-21386" />
                <div className="absolute inset-6 rounded-full border border-purple-500/15 animate-[spin_45s_linear_infinite_reverse]" data-id="element-21387" />
                <div className="absolute inset-12 rounded-full border border-indigo-400/10 animate-[spin_35s_linear_infinite]" data-id="element-21388" />
                <div className="w-48 h-48 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full blur-3xl opacity-30 animate-pulse" data-id="element-21389" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-21390">
                  <div className="relative" data-id="element-21391">
                    <Sun className="w-20 h-20 text-amber-300/70" strokeWidth={1} data-id="element-21392" />
                    <Moon className="w-10 h-10 text-slate-300/60 absolute -top-2 -right-4" strokeWidth={1} data-id="element-21393" />
                  </div>
                </div>
                {/* Orbiting dots */}
                {[0, 60, 120, 180, 240, 300].map((deg, i) => <div key={i} className="absolute w-2 h-2 rounded-full bg-indigo-400/40" style={{
                top: `${50 + 45 * Math.sin(deg * Math.PI / 180)}%`,
                left: `${50 + 45 * Math.cos(deg * Math.PI / 180)}%`,
                transform: 'translate(-50%, -50%)'
              }} data-id="element-21394" />)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* 2. ЧТО ТАКОЕ НАТАЛЬНАЯ КАРТА */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-21395">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21396">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-6 sm:mb-8 text-center" data-id="element-21397">
            Что такое натальная карта
          </h2>
          <div className="prose prose-lg prose-slate max-w-none text-slate-600 space-y-6" data-id="element-21398">
            <p data-id="element-21399">
              Натальная карта (или карта рождения) — это астрологическая схема,
              которая фиксирует точное положение всех планет Солнечной системы в
              момент вашего рождения. Это своего рода «космический паспорт»,
              уникальный для каждого человека. Даже близнецы, рождённые с
              разницей в несколько минут, могут иметь существенные отличия в
              своих картах.
            </p>
            <p data-id="element-21400">
              Для расчёта натальной карты используются три ключевых параметра:{' '}
              <strong data-id="element-21401">дата рождения</strong>,{' '}
              <strong data-id="element-21402">точное время рождения</strong> и{' '}
              <strong data-id="element-21403">место рождения</strong>. На основе этих данных
              астрономические алгоритмы определяют, в каких знаках зодиака и
              домах гороскопа находились планеты, какие аспекты (углы) они
              образовывали между собой.
            </p>
            <p data-id="element-21404">
              Каждая планета в карте отвечает за определённую сферу жизни:
              Солнце — за ядро личности, Луна — за эмоции, Венера — за любовь и
              ценности, Марс — за энергию и амбиции. Их положение в знаках и
              домах создаёт неповторимый рисунок вашей судьбы, характера и
              потенциала.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 3. ЧТО МОЖНО УЗНАТЬ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-21405">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21406">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-21407">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21408">
              Что можно узнать из натальной карты
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-21409">
              Каждый раздел отчёта раскрывает отдельную грань вашей личности
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto" data-id="element-21410">
            {[{
            icon: <User className="w-5 h-5" data-id="element-21411" />,
            color: 'bg-indigo-50 text-indigo-600',
            title: 'Личность и характер',
            desc: 'Описание психологических особенностей, темперамента, способа мышления и восприятия мира.'
          }, {
            icon: <Moon className="w-5 h-5" data-id="element-21412" />,
            color: 'bg-purple-50 text-purple-600',
            title: 'Эмоции и внутренний мир',
            desc: 'Как вы реагируете на события, что вас тревожит, как вы строите эмоциональные связи с людьми.'
          }, {
            icon: <Heart className="w-5 h-5" data-id="element-21413" />,
            color: 'bg-rose-50 text-rose-600',
            title: 'Любовь и отношения',
            desc: 'Как вы проявляете себя в отношениях, какие партнёры вам подходят и что мешает гармонии.'
          }, {
            icon: <Briefcase className="w-5 h-5" data-id="element-21414" />,
            color: 'bg-emerald-50 text-emerald-600',
            title: 'Карьера и предназначение',
            desc: 'Какие сферы деятельности подходят лучше всего, где искать финансовый успех и реализацию.'
          }, {
            icon: <Sparkles className="w-5 h-5" data-id="element-21415" />,
            color: 'bg-amber-50 text-amber-600',
            title: 'Таланты и сильные стороны',
            desc: 'Природные способности, скрытый потенциал и ресурсы, которые можно активировать.'
          }, {
            icon: <Compass className="w-5 h-5" data-id="element-21416" />,
            color: 'bg-sky-50 text-sky-600',
            title: 'Жизненные задачи',
            desc: 'Кармические уроки, вектор развития и ключевые темы, с которыми вы пришли в этот мир.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex items-start gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-21417">
                <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-21418">
                  {item.icon}
                </div>
                <div className="min-w-0" data-id="element-21419">
                  <h3 className="text-base font-bold text-slate-900 mb-1" data-id="element-21420">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-21421">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 4. КАК РАССЧИТЫВАЕТСЯ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-21422">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21423">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-21424">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21425">
              Как рассчитывается натальная карта
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-21426">
              Пять простых шагов от данных до готового отчёта
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto" data-id="element-21427">
            {[{
            step: '01',
            icon: <Calendar className="w-5 h-5" data-id="element-21428" />,
            color: 'bg-indigo-50 text-indigo-600',
            stepColor: 'text-indigo-500',
            title: 'Дата рождения',
            desc: 'Укажите точную дату вашего рождения'
          }, {
            step: '02',
            icon: <Clock className="w-5 h-5" data-id="element-21429" />,
            color: 'bg-purple-50 text-purple-600',
            stepColor: 'text-purple-500',
            title: 'Время рождения',
            desc: 'Введите время с точностью до минут'
          }, {
            step: '03',
            icon: <MapPin className="w-5 h-5" data-id="element-21430" />,
            color: 'bg-emerald-50 text-emerald-600',
            stepColor: 'text-emerald-500',
            title: 'Место рождения',
            desc: 'Укажите город и страну рождения'
          }, {
            step: '04',
            icon: <Star className="w-5 h-5" data-id="element-21431" />,
            color: 'bg-amber-50 text-amber-600',
            stepColor: 'text-amber-500',
            title: 'Расчёт карты',
            desc: 'Система определяет положение планет'
          }, {
            step: '05',
            icon: <FileText className="w-5 h-5" data-id="element-21432" />,
            color: 'bg-rose-50 text-rose-600',
            stepColor: 'text-rose-500',
            title: 'Готовый отчёт',
            desc: 'Получите подробный астрологический анализ'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex items-center gap-4 bg-slate-50 rounded-xl px-5 py-4 border border-slate-100" data-id="element-21433">
                <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-21434">
                  {item.icon}
                </div>
                <div className="min-w-0 flex-1" data-id="element-21435">
                  <div className="flex items-center gap-2 mb-0.5" data-id="element-21436">
                    <span className={`text-xs font-bold ${item.stepColor}`} data-id="element-21437">
                      {item.step}
                    </span>
                    <h3 className="text-base font-bold text-slate-900" data-id="element-21438">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-slate-500 text-sm" data-id="element-21439">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 5. ЧТО ВХОДИТ В ОТЧЁТ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-21440">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21441">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-21442">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21443">
              Что входит в астрологический отчёт
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-21444">
              Подробный документ на 30-40 страниц, охватывающий все ключевые
              элементы вашей натальной карты
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-5xl mx-auto" data-id="element-21445">
            {[{
            icon: <Sun className="w-5 h-5" data-id="element-21446" />,
            text: 'Анализ Солнца, Луны и Асцендента'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-21447" />,
            text: 'Положение планет в знаках зодиака'
          }, {
            icon: <BookOpen className="w-5 h-5" data-id="element-21448" />,
            text: 'Планеты в домах гороскопа'
          }, {
            icon: <Gem className="w-5 h-5" data-id="element-21449" />,
            text: 'Аспекты между планетами'
          }, {
            icon: <Heart className="w-5 h-5" data-id="element-21450" />,
            text: 'Анализ сферы отношений'
          }, {
            icon: <Briefcase className="w-5 h-5" data-id="element-21451" />,
            text: 'Карьерный потенциал и финансы'
          }, {
            icon: <Compass className="w-5 h-5" data-id="element-21452" />,
            text: 'Жизненные задачи и кармические узлы'
          }, {
            icon: <Sparkles className="w-5 h-5" data-id="element-21453" />,
            text: 'Таланты и скрытые способности'
          }, {
            icon: <Shield className="w-5 h-5" data-id="element-21454" />,
            text: 'Рекомендации и компенсаторика'
          }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-21455">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-21456">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-21457">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 6. ПРИМЕР ОТЧЁТА */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-21458">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21459">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-21460">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-21461">
              <div className="p-6 sm:p-10 lg:p-16 flex flex-col justify-center" data-id="element-21462">
                <span className="text-xs font-bold text-indigo-300 uppercase tracking-[0.2em] mb-3 sm:mb-4" data-id="element-21463">
                  Пример отчёта
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-id="element-21464">
                  Профессиональный отчёт, а не общие фразы
                </h2>
                <p className="text-slate-400 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed" data-id="element-21465">
                  Каждый отчёт содержит графическую схему натальной карты,
                  подробный анализ всех планет и домов, а также практические
                  рекомендации.
                </p>
                <ul className="space-y-3 mb-6 sm:mb-10" data-id="element-21466">
                  {['Графическая схема натальной карты', 'Персональный анализ каждой планеты', 'Практические советы и рекомендации', 'Красивое оформление, удобно читать'].map((t, i) => <li key={i} className="flex items-center text-slate-300 text-sm" data-id="element-21467">
                      <Check className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" data-id="element-21468" />
                      {t}
                    </li>)}
                </ul>
                <Button size="lg" variant="outline" className="w-fit bg-white border-0 hover:bg-slate-100" onClick={() => onNavigate('report-preview-natal')} data-id="element-21469">
                  Посмотреть пример PDF
                </Button>
              </div>
              <div className="bg-slate-800/50 p-4 sm:p-8 lg:p-12 flex items-center justify-center" data-id="element-21470">
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="w-full max-w-sm bg-white rounded-xl shadow-2xl p-8" data-id="element-21471">
                  <div className="w-full h-32 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg mb-6 flex items-center justify-center relative overflow-hidden" data-id="element-21472">
                    <div className="w-20 h-20 rounded-full border-2 border-indigo-200 flex items-center justify-center" data-id="element-21473">
                      <Sun className="w-8 h-8 text-indigo-300" data-id="element-21474" />
                    </div>
                    <Moon className="w-5 h-5 text-purple-300 absolute top-4 right-6" data-id="element-21475" />
                    <Star className="w-3 h-3 text-amber-300 absolute bottom-6 left-8" data-id="element-21476" />
                  </div>
                  <div className="w-3/4 h-3 bg-slate-200 rounded mb-3" data-id="element-21477" />
                  <div className="w-1/2 h-3 bg-slate-100 rounded mb-6" data-id="element-21478" />
                  <div className="space-y-2.5" data-id="element-21479">
                    <div className="w-full h-2 bg-slate-100 rounded" data-id="element-21480" />
                    <div className="w-full h-2 bg-slate-100 rounded" data-id="element-21481" />
                    <div className="w-5/6 h-2 bg-slate-50 rounded" data-id="element-21482" />
                    <div className="w-full h-2 bg-slate-100 rounded" data-id="element-21483" />
                    <div className="w-3/4 h-2 bg-slate-50 rounded" data-id="element-21484" />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 7. ПОЧЕМУ ВАЖНО ЗНАТЬ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-21485">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21486">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-21487">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21488">
              Почему важно знать свою натальную карту
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto" data-id="element-21489">
            {[{
            icon: <Eye className="w-6 h-6" data-id="element-21490" />,
            color: 'bg-indigo-100 text-indigo-600',
            title: 'Лучшее понимание себя',
            desc: 'Вы увидите свои истинные мотивации, страхи и потребности — не навязанные обществом, а заложенные природой.'
          }, {
            icon: <Clock className="w-6 h-6" data-id="element-21491" />,
            color: 'bg-amber-100 text-amber-600',
            title: 'Понимание жизненных циклов',
            desc: 'Узнаете, почему определённые периоды жизни были сложными и когда наступят благоприятные перемены.'
          }, {
            icon: <Sparkles className="w-6 h-6" data-id="element-21492" />,
            color: 'bg-emerald-100 text-emerald-600',
            title: 'Осознание сильных сторон',
            desc: 'Поймёте, какие таланты и ресурсы у вас уже есть и как их использовать для достижения целей.'
          }, {
            icon: <Compass className="w-6 h-6" data-id="element-21493" />,
            color: 'bg-rose-100 text-rose-600',
            title: 'Помощь в выборе пути',
            desc: 'Получите ориентиры для принятия важных решений в карьере, отношениях и личном развитии.'
          }].map((item, i) => <Card key={i} padding="lg" className="border-slate-100" data-id="element-21494">
                <div className="flex items-start gap-5" data-id="element-21495">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-21496">
                    {item.icon}
                  </div>
                  <div data-id="element-21497">
                    <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-21498">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-21499">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 8. ДЛЯ КОГО */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-21500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21501">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-21502">
            Для кого подходит натальная карта
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-21503">
            {[{
            icon: <Search className="w-5 h-5" data-id="element-21504" />,
            text: 'Людям, которые хотят лучше понять себя и свои реакции'
          }, {
            icon: <Lightbulb className="w-5 h-5" data-id="element-21505" />,
            text: 'Тем, кто ищет своё предназначение и подходящую профессию'
          }, {
            icon: <Heart className="w-5 h-5" data-id="element-21506" />,
            text: 'Тем, кто хочет разобраться в отношениях и найти гармонию'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-21507" />,
            text: 'Тем, кто интересуется астрологией и хочет изучить свою карту'
          }, {
            icon: <Users className="w-5 h-5" data-id="element-21508" />,
            text: 'Родителям, которые хотят лучше понять своих детей'
          }, {
            icon: <Target className="w-5 h-5" data-id="element-21509" />,
            text: 'Тем, кто стоит перед важным жизненным выбором'
          }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-slate-50 rounded-xl px-5 py-4" data-id="element-21510">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-21511">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-21512">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 9. CTA — ПРИЗЫВ К ДЕЙСТВИЮ */}
      {/* ═══════════════════════════════════════════ */}
      <div ref={ctaRef} className="scroll-mt-10" data-id="element-21513">
        <AnimatedSection className="py-14 sm:py-24 bg-slate-900" data-id="element-21514">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-21515">
            <motion.div animate={{
            rotate: [0, 10, -10, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-21516">
              <Star className="w-8 h-8 text-amber-400 mx-auto mb-4 sm:mb-6" data-id="element-21517" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6" data-id="element-21518">
              Рассчитайте свою натальную карту
            </h2>
            <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-21519">
              Введите дату, время и место рождения, чтобы получить персональный
              астрологический анализ вашей личности, талантов и жизненного пути.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-21520">
              <Button size="lg" onClick={scrollToPricing} className="text-base sm:text-lg px-10 shadow-lg shadow-indigo-500/25" data-id="element-21521">
                Построить мою натальную карту
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-21522" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500" data-id="element-21523">
              <span className="flex items-center" data-id="element-21524">
                <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-21525" /> Данные
                защищены
              </span>
              <span className="flex items-center" data-id="element-21526">
                <Clock className="w-4 h-4 mr-1.5 text-indigo-400" data-id="element-21527" /> Результат
                мгновенно
              </span>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* 10. ТАРИФЫ — replaced with ServicePurchaseSection */}
      <div ref={pricingRef} className="scroll-mt-10" data-id="element-21528">
        <ServicePurchaseSection serviceType="natal" onNavigate={onNavigate} data-id="element-21529" />
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* FAQ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-21530">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21531">
          <div className="text-center mb-8 sm:mb-12" data-id="element-21532">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-21533">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Нужно ли знать точное время рождения?',
          content: 'Да, для точного расчёта натальной карты желательно знать время рождения до минут. Время влияет на положение Асцендента и домов гороскопа. Если вы не знаете точного времени, наши астрологи могут провести процедуру ректификации (восстановление времени по событиям жизни).'
        }, {
          id: '2',
          title: 'Чем Beta отчёт отличается от Premium?',
          content: 'Beta отчёт содержит анализ трёх ключевых показателей (Солнце, Луна, Асцендент) и общие рекомендации — это 1-3 страницы. Premium отчёт — это полный профессиональный разбор всех планет, домов, аспектов, кармических задач и практических рекомендаций на 30-40 страницах.'
        }, {
          id: '3',
          title: 'Как быстро я получу отчёт?',
          content: 'Мгновенно. Сразу после оплаты система сгенерирует PDF-файл. Вы сможете скачать его на странице успеха, также он будет отправлен на email и сохранится в личном кабинете.'
        }, {
          id: '4',
          title: 'Безопасно ли оставлять свои данные?',
          content: 'Абсолютно. Мы используем ваши данные (дата, время, место рождения) исключительно для математического расчёта положения планет. Мы не передаём их третьим лицам.'
        }, {
          id: '5',
          title: 'Можно ли заказать отчёт для другого человека?',
          content: 'Да! Вы можете заказать отчёт для другого человека, партнёра или ребёнка. Главное — знать их точные данные рождения (дату, время и место).'
        }]} data-id="element-21534" />
        </div>
      </AnimatedSection>
    </div>;
}